import React, { useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'axios'
import {
  makeStyles,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Divider,
  Link as MuiLink
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'

const V4_T = {
  cream: '#f4efe4',
  surface: '#fbf8f1',
  surface2: '#faf6ee',
  line: '#e7dfc9',
  lineSoft: '#ede6d2',
  ink: '#1c2419',
  ink2: '#3d463a',
  mute: '#6f7a6b',
  muteSoft: '#97a094',
  green: '#1f5e3a',
  greenSoft: '#e3eee0',
  greenSofter: '#eef5eb',
  rust: '#a9341d',
  rustSoft: '#fbeee6',
  orange: '#c66a45',
  amber: '#d8973a',
  plum: '#6e3fb7',
  blue: '#3454c1',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace'
}

const useStyles = makeStyles(theme => ({
  drawer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: V4_T.cream,
    color: V4_T.ink
  },
  header: {
    padding: '20px 24px 16px',
    backgroundColor: V4_T.surface,
    borderBottom: `1px solid ${V4_T.line}`
  },
  title: {
    fontWeight: 800,
    fontSize: 18,
    color: V4_T.ink,
    lineHeight: 1.2
  },
  subtitle: {
    fontSize: 12,
    color: V4_T.mute,
    marginTop: 4
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  tabs: {
    backgroundColor: V4_T.surface,
    borderBottom: `1px solid ${V4_T.line}`,
    minHeight: 40,
    '& .MuiTab-root': {
      minHeight: 40,
      fontSize: 12,
      fontWeight: 700,
      textTransform: 'none',
      color: V4_T.muteSoft
    },
    '& .Mui-selected': {
      color: V4_T.ink
    }
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '8px'
  },
  kpiCard: {
    padding: '12px 10px',
    background: V4_T.cream,
    border: `1px solid ${V4_T.line}`,
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  kpiLabel: {
    fontSize: 9,
    fontWeight: 800,
    color: V4_T.muteSoft,
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
    marginBottom: 4
  },
  kpiValue: {
    fontSize: 18,
    fontWeight: 800,
    color: V4_T.ink,
    fontFamily: V4_T.mono
  },
  section: {
    marginBottom: 8
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 800,
    color: V4_T.muteSoft,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 10
  },
  card: {
    background: V4_T.surface,
    borderRadius: 10,
    border: `1px solid ${V4_T.line}`,
    padding: '14px 16px'
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 0',
    borderBottom: `1px solid ${V4_T.lineSoft}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  statLabel: {
    fontSize: 12,
    color: V4_T.mute,
    fontWeight: 500
  },
  statValue: {
    fontSize: 12,
    color: V4_T.ink,
    fontWeight: 700
  }
}))

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return '-'
  return date.toLocaleDateString()
}

function PropertyDetailDrawer({ propertyId, onClose, url }) {
  const classes = useStyles()
  const [tab, setTab] = useState(0)
  const [loading, setLoading] = useState(true)
  const [property, setProperty] = useState(null)
  const [history, setHistory] = useState([])
  const [opportunities, setOpportunities] = useState([])

  const fetchData = useCallback(async () => {
    if (!propertyId) return
    setLoading(true)
    try {
      const [propRes, histRes, oppRes] = await Promise.all([
        axios.get(`${url}/api/aspire/routing/properties/${propertyId}`),
        axios.get(`${url}/api/aspire/routing/properties/${propertyId}/visit-history`),
        axios.get(`${url}/api/aspire/routing/properties/${propertyId}/opportunities`)
      ])
      setProperty(propRes.data)
      setHistory(histRes.data.visits || [])
      setOpportunities(oppRes.data.data || [])
    } catch (err) {
      console.error('Failed to fetch property details:', err)
    } finally {
      setLoading(false)
    }
  }, [propertyId, url])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress size={32} thickness={5} style={{ color: V4_T.green }} />
      </Box>
    )
  }

  if (!property) return null

  return (
    <div className={classes.drawer}>
      <div className={classes.header}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <div>
            <Typography className={classes.title}>{property.name}</Typography>
            <Typography className={classes.subtitle}>{property.address1}</Typography>
          </div>
          <IconButton onClick={onClose} size="small" style={{ marginTop: -4, marginRight: -8 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box mt={2} display="flex" gap={12}>
           <MuiLink 
            href={`https://cloud.youraspire.com/app/properties/overview/${property.propertyId}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 11, fontWeight: 700, color: V4_T.green, display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}
          >
            VIEW IN ASPIRE <OpenInNewIcon style={{ fontSize: 11 }} />
          </MuiLink>
        </Box>
      </div>

      <Tabs 
        value={tab} 
        onChange={(e, v) => setTab(v)} 
        className={classes.tabs}
        variant="fullWidth"
        indicatorColor="primary"
      >
        <Tab label="OVERVIEW" />
        <Tab label="HISTORY" />
        <Tab label="FINANCIALS" />
      </Tabs>

      <div className={classes.content}>
        {tab === 0 && (
          <>
            <div className={classes.kpiGrid}>
              <KPIRibbon label="Margin" value={`${property.profitMargin?.toFixed(1)}%`} color={property.profitMargin < 30 ? V4_T.rust : V4_T.green} />
              <KPIRibbon label="Est Hrs" value={`${property.estimatedHours?.toFixed(1)}h`} />
              <KPIRibbon label="Avg Hrs" value={`${property.avgOnSiteHours?.toFixed(1)}h`} />
            </div>
            
            <div className={classes.section}>
              <div className={classes.sectionTitle}>Maintenance Details</div>
              <div className={classes.card}>
                <StatRow label="Annual Target" value={`${property.targetAnnualVisits} visits`} />
                <StatRow label="Completed" value={`${property.completedOccurrences} visits`} />
                <StatRow label="Contract Price" value={formatCurrency(property.contractPricePerVisit)} />
                <StatRow label="Primary Crew" value={property.primaryCrewLeader?.name || 'Unassigned'} />
              </div>
            </div>

            <div className={classes.section}>
              <div className={classes.sectionTitle}>Service History</div>
              <div className={classes.card}>
                <StatRow label="Last Maintenance" value={formatDate(property.lastMaintenanceVisitDate)} />
                <StatRow label="Last Irrigation" value={formatDate(property.lastIrrigationVisitDate)} />
                <StatRow label="Last Herbicide" value={formatDate(property.lastHerbicideVisitDate)} />
                <StatRow label="Next Visit" value={formatDate(property.nextVisitDate)} />
              </div>
            </div>
          </>
        )}

        {tab === 1 && (
          <div className={classes.section}>
            <div className={classes.sectionTitle}>Recent Visits</div>
            <div className={classes.card} style={{ padding: '0 16px' }}>
              {history.length === 0 ? (
                <Box py={3} textAlign="center" color={V4_T.muteSoft} fontSize={12}>No recent visits found.</Box>
              ) : (
                history.slice(0, 15).map((v, i) => (
                  <Box key={i} py={1.5} borderBottom={i < Math.min(history.length, 15) - 1 ? `1px solid ${V4_T.lineSoft}` : 'none'}>
                    <Box display="flex" justifyContent="space-between" alignItems="baseline">
                      <Typography variant="subtitle2" style={{ fontWeight: 800, fontSize: 13 }}>
                        {new Date(v.VisitDate || v.ScheduledDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="subtitle2" style={{ fontFamily: V4_T.mono, fontWeight: 700, color: V4_T.ink }}>
                        {(v.HoursAct || v.HoursEst || 0).toFixed(1)}h
                      </Typography>
                    </Box>
                    <Typography variant="caption" style={{ color: V4_T.mute, fontWeight: 600, display: 'block', marginTop: 2 }}>
                      {v.ServiceName || v.ServiceDisplayName}
                    </Typography>
                    <Typography variant="caption" style={{ color: V4_T.muteSoft, fontSize: 10 }}>
                      {v.CrewLeaderName || 'Crew pending'}
                    </Typography>
                  </Box>
                ))
              )}
            </div>
          </div>
        )}

        {tab === 2 && (
          <>
            <div className={classes.kpiGrid}>
              <KPIRibbon label="Earned Rev" value={formatCompactCurrency(property.totalEarnedRevenue)} />
              <KPIRibbon label="Actual Labor" value={formatCompactCurrency(property.totalLaborCost)} />
              <KPIRibbon label="Net Profit" value={formatCompactCurrency(property.netProfit)} color={property.netProfit < 0 ? V4_T.rust : V4_T.green} />
            </div>
            
            <div className={classes.section}>
              <div className={classes.sectionTitle}>Revenue Breakdown</div>
              <div className={classes.card}>
                <StatRow label="Maintenance Earned" value={formatCurrency(property.maintenanceEarnedRevenue)} />
                <StatRow label="Other Won Rev" value={formatCurrency(property.otherEarnedRevenue)} />
              </div>
            </div>

            <div className={classes.section}>
              <div className={classes.sectionTitle}>Won Opportunities</div>
              <div className={classes.card} style={{ padding: '0 16px' }}>
                {opportunities.length === 0 ? (
                  <Box py={3} textAlign="center" color={V4_T.muteSoft} fontSize={12}>No opportunities found.</Box>
                ) : (
                  opportunities.map((o, i) => (
                    <Box key={i} py={1.5} borderBottom={i < opportunities.length - 1 ? `1px solid ${V4_T.lineSoft}` : 'none'}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Typography variant="subtitle2" style={{ fontWeight: 800, fontSize: 12, flex: 1, paddingRight: 8 }}>{o.OpportunityName}</Typography>
                        <Typography variant="subtitle2" style={{ fontWeight: 800, color: V4_T.green }}>{formatCurrency(o.WonDollars)}</Typography>
                      </Box>
                      <Typography variant="caption" style={{ color: V4_T.mute, display: 'block', marginTop: 2 }}>
                        {o.DivisionName} · {o.OpportunityStatusName}
                      </Typography>
                    </Box>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function KPIRibbon({ label, value, color = V4_T.ink }) {
  const classes = useStyles()
  return (
    <div className={classes.kpiCard}>
      <div className={classes.kpiLabel}>{label}</div>
      <div className={classes.kpiValue} style={{ color }}>{value}</div>
    </div>
  )
}

function StatRow({ label, value }) {
  const classes = useStyles()
  return (
    <div className={classes.statRow}>
      <span className={classes.statLabel}>{label}</span>
      <span className={classes.statValue}>{value}</span>
    </div>
  )
}

const formatCompactCurrency = (value) => {
  const num = Number(value) || 0
  if (Math.abs(num) >= 1000) return `$${(num / 1000).toFixed(1)}k`
  return formatCurrency(num)
}

export default PropertyDetailDrawer
